import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeText,
  TeDropdown,
  TeDropdownOption,
} from '@topcon/tuicl-react';

/**
 * Generic interface for paginated API responses
 */
export interface PaginatedResponse<T> {
  /** Current page index */
  page: number;
  /** Current page size */
  limit: number;
  /** Total number of items */
  total: number;
  /** Array of items */
  items: T[];
  [key: string]: unknown;
}

interface Props<T> {
  /** The paginated response data */
  data: PaginatedResponse<T>;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when items per page changes */
  onItemsPerPageChange: (itemsPerPage: number) => void;
  /** Available items per page options (default: [10, 25, 50, 100]) */
  itemsPerPageOptions?: number[];
  /** Maximum number of visible page buttons (default: 5) */
  maxVisiblePages?: number;
}

function GenericPagination<T>({
  data,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  maxVisiblePages = 5,
}: Props<T>) {
  const { page: currentPage, limit: itemsPerPage, total: totalItems } = data;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, and pages around current
      pages.push(1);

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <TeContainer
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="background.primary"
      borderRadius={4}
      fill
      height="xl"
      padding="s"
    >
      {/* Items per page selector */}
      <TeContainerSlim alignItems="center" gap="xs">
        <TeDropdown
          required={true}
          size="s"
          value={itemsPerPage.toString()}
          techange={(e) => onItemsPerPageChange(Number(e.value))}
          persistent
        >
          {itemsPerPageOptions.map((option) => (
            <TeDropdownOption key={option} value={option.toString()} text={option.toString()} />
          ))}
        </TeDropdown>
      </TeContainerSlim>

      {/* Pagination controls */}
      <TeContainerSlim alignItems="center" gap="xs">
        <TeButton
          variant="neutral"
          size="s"
          icon="chevron_left"
          teclick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
        />

        {pageNumbers.map((page, index) => (
          <TeContainerSlim key={index}>
            {page === '...' ? (
              <TeContainerSlim padding="xs">
                <TeText size={14} color="text.secondary">
                  ...
                </TeText>
              </TeContainerSlim>
            ) : (
              <TeButton
                variant={page === currentPage ? 'primary' : 'neutral'}
                size="s"
                text={page.toString()}
                teclick={() => onPageChange(page as number)}
              />
            )}
          </TeContainerSlim>
        ))}

        <TeButton
          variant="neutral"
          size="s"
          icon="chevron_right"
          teclick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
        />
      </TeContainerSlim>
    </TeContainer>
  );
}

export default GenericPagination;
